import { createContext, useContext, useEffect, useRef, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import {
  clamp,
  findIndex,
  isEmpty,
  isNil,
  keyBy,
  map,
  omitBy,
  some,
  uniq,
} from "lodash";
import { toast } from "sonner";

import { isAssetsListMediaTypeAllowed } from "@kagami/shared/assetsList";

import { apiFetch } from "@/lib/api";
import { parseContentDispositionFilename } from "@/lib/downloadFile";
import useAssetsDownload from "@/hooks/useAssetsDownload";
import { useShow } from "@/contexts/ShowContext";
import {
  createAssetsList,
  deleteAssetsList,
  fetchAssetsList,
  fetchAssetsListAssets,
  updateAssetsList,
} from "@/components/AssetsList/AssetsListApiClient";
import {
  ASSETS_LIST_DRAFT_STORAGE_PREFIX,
  ASSETS_LIST_EXPANDED_STORAGE_KEY,
  DEFAULT_ASSETS_LIST_EXPANDED,
  AssetsListSaveOutcome,
  DEFAULT_ASSETS_LIST_NAME,
  DEFAULT_ASSETS_LIST_VISIBILITY,
  UNSAVED_ASSETS_LIST_LABEL,
} from "@/components/AssetsList/constants";

const AssetsListContext = createContext(null);

const draftStorageKey = (showId) =>
  `${ASSETS_LIST_DRAFT_STORAGE_PREFIX}.${showId}`;

function createEmptyWorkingList() {
  return {
    assetsListId: null,
    name: DEFAULT_ASSETS_LIST_NAME,
    visibility: DEFAULT_ASSETS_LIST_VISIBILITY,
    entries: [],
    // A saved list re-fetches authoritative assets on load (see the hydrate
    // effect); a never-saved list has no id to fetch by, so this starts empty.
    assetsById: {},
    isDirty: false,
  };
}

/**
 * @input 's1'
 * @output {assetsListId: 'l1', name: 'Opening', visibility: 'private', entries: [...]}
 */
function readPersistedDraft(showId) {
  try {
    const raw = localStorage.getItem(draftStorageKey(showId));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!Array.isArray(parsed.entries)) return null;

    return parsed;
  } catch {
    return null;
  }
}

function writePersistedDraft({ showId, workingList }) {
  try {
    const draft = {
      assetsListId: workingList.assetsListId,
      name: workingList.name,
      visibility: workingList.visibility,
      entries: workingList.entries,
      isDirty: workingList.isDirty,
    };
    localStorage.setItem(draftStorageKey(showId), JSON.stringify(draft));
  } catch {}
}

/**
 * @input localStorage "true"
 * @output true
 *
 * @input localStorage empty
 * @output false
 */
function readPersistedExpanded() {
  try {
    const savedValue = localStorage.getItem(ASSETS_LIST_EXPANDED_STORAGE_KEY);
    if (savedValue === "true") {
      return true;
    }
    if (savedValue === "false") {
      return false;
    }
  } catch {
    return DEFAULT_ASSETS_LIST_EXPANDED;
  }
  return DEFAULT_ASSETS_LIST_EXPANDED;
}

function writePersistedExpanded(isExpanded) {
  try {
    localStorage.setItem(ASSETS_LIST_EXPANDED_STORAGE_KEY, String(isExpanded));
  } catch {}
}

function resolveDraftIsDirty(draft) {
  if (typeof draft.isDirty === "boolean") {
    return draft.isDirty;
  }

  return !draft.assetsListId && !isEmpty(draft.entries);
}

/**
 * The player measures these; nothing else can. Absent measurements stay null
 * rather than becoming zero, so the exporter can tell "never measured" apart
 * from a real value.
 *
 * @input {frameRate: 24, duration: 9.4, width: 1920, height: 1080}
 * @output {source_frame_rate: 24, source_duration: 9.4, source_width: 1920, source_height: 1080}
 */
function toEntrySourceMedia(sourceMedia) {
  return {
    source_frame_rate: sourceMedia?.frameRate ?? null,
    source_duration: sourceMedia?.duration ?? null,
    source_width: sourceMedia?.width ?? null,
    source_height: sourceMedia?.height ?? null,
  };
}

/**
 * @input {asset: {_id: 'a1'}, inTime: 2, outTime: 5, sourceMedia: {frameRate: 24}}
 * @output {entryKey: '0f1c…', asset_id: 'a1', in_time: 2, out_time: 5, source_frame_rate: 24, …}
 */
function createWorkingEntry({
  asset,
  inTime = null,
  outTime = null,
  sourceMedia,
}) {
  return {
    entryKey: crypto.randomUUID(),
    asset_id: asset._id,
    in_time: inTime,
    out_time: outTime,
    ...toEntrySourceMedia(sourceMedia),
  };
}

/**
 * @input {_id: 'a1'}
 * @output {entryKey: '0f1c…', asset_id: 'a1', in_time: null, out_time: null, source_frame_rate: null, …}
 */
function toWorkingEntry(asset) {
  return createWorkingEntry({ asset });
}

/**
 * A save round-trips the list through the api, which hands back entries keyed
 * afresh — so the open preview's key matches nothing on the way back. Saving
 * never reorders, so the entry at the same position is the same entry, and
 * re-keying the preview to it leaves the panel exactly where the user left it.
 *
 * @input {previewEntryKey: 'k1', previousEntries: [{entryKey: 'k1'}], nextEntries: [{entryKey: 'k9'}]}
 * @output 'k9'
 */
function findPreservedPreviewEntryKey({
  previewEntryKey,
  previousEntries,
  nextEntries,
}) {
  if (!previewEntryKey) return null;

  const previewedIndex = findIndex(
    previousEntries,
    (entry) => entry.entryKey === previewEntryKey,
  );
  if (previewedIndex === -1) return null;

  return nextEntries[previewedIndex]?.entryKey ?? null;
}

/**
 * @input {status: 400, message: 'Failed to create assets list "x": Assets list is too long'}
 * @output 'Assets list is too long'
 */
function saveFailureMessage(error) {
  if (error?.status !== 400) return "Failed to save list";

  const apiMessage = String(error.message).split(": ").slice(1).join(": ");
  if (!apiMessage) return "Failed to save list";

  return apiMessage;
}

/**
 * @input Response({status: 501, body: '{"error":"Edit decision list generation is not implemented yet."}'})
 * @output 'Edit decision list generation is not implemented yet.'
 */
async function readDownloadErrorMessage(response) {
  try {
    const body = await response.json();
    if (body && typeof body.error === "string") return body.error;
  } catch {}
  return "Failed to download EDL";
}

export function AssetsListProvider({ children }) {
  const { show } = useShow();

  const [workingList, setWorkingList] = useState(createEmptyWorkingList);
  const [isExpanded, setIsExpanded] = useState(readPersistedExpanded);

  useEffect(() => {
    writePersistedExpanded(isExpanded);
  }, [isExpanded]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreparingDownload, setIsPreparingDownload] = useState(false);
  // Which list entry the overlay preview is showing, or null when it is shut.
  // Keyed on the entry rather than the asset because one list can hold the
  // same asset several times with different trims.
  const [previewEntryKey, setPreviewEntryKey] = useState(null);

  const showId = show?._id ?? null;

  // Guards both directions: hydrate once per show, and never persist before
  // hydrating — that would overwrite the stored draft with the empty initial state.
  const hydratedShowIdRef = useRef(null);

  useEffect(() => {
    if (!showId) return;
    if (hydratedShowIdRef.current === showId) return;

    hydratedShowIdRef.current = showId;

    const draft = readPersistedDraft(showId);
    if (!draft) {
      setWorkingList(createEmptyWorkingList());
      return;
    }

    setWorkingList({
      ...createEmptyWorkingList(),
      assetsListId: draft.assetsListId ?? null,
      name: draft.name ?? DEFAULT_ASSETS_LIST_NAME,
      visibility: draft.visibility ?? DEFAULT_ASSETS_LIST_VISIBILITY,
      entries: draft.entries,
      isDirty: resolveDraftIsDirty(draft),
    });

    // The strip's assets come from the list's own endpoint, never from the
    // grid — the grid holds one page, so paging away from an asset used to
    // empty its box. A list with unsaved changes asks by its entries' ids,
    // because the saved list either doesn't exist yet or no longer matches
    // what the strip is showing.
    const isUnsaved = !draft.assetsListId || resolveDraftIsDirty(draft);

    fetchAssetsListAssets({
      showId,
      assetsListId: draft.assetsListId,
      assetIds: isUnsaved ? uniq(map(draft.entries, "asset_id")) : [],
    })
      .then((assets) => cacheAssets({ assets }))
      .catch((error) => {
        console.error("Failed to load assets for the working list", error);
      });
  }, [showId]);

  useEffect(() => {
    if (!showId) return;
    if (hydratedShowIdRef.current !== showId) return;

    writePersistedDraft({ showId, workingList });
  }, [showId, workingList]);

  // Everything is derived from `current` inside the updater, never from the
  // render-time `workingList`: two drops batched into one render would otherwise
  // both build off the same stale array and the first one's entries would vanish.
  const insertAssetsAt = ({ index, assets }) => {
    const droppedAssets = (assets || []).filter(
      (asset) => asset && isAssetsListMediaTypeAllowed(asset.mediaType),
    );
    if (
      (assets || []).some(
        (asset) => !isAssetsListMediaTypeAllowed(asset?.mediaType),
      )
    ) {
      toast.error("Audio assets can't be added to a list");
    }
    if (isEmpty(droppedAssets)) return;

    setWorkingList((current) => {
      const insertionIndex = clamp(index, 0, current.entries.length);
      const entriesBefore = current.entries.slice(0, insertionIndex);
      const entriesAfter = current.entries.slice(insertionIndex);
      const insertedEntries = droppedAssets.map(toWorkingEntry);

      return {
        ...current,
        entries: [...entriesBefore, ...insertedEntries, ...entriesAfter],
        assetsById: { ...current.assetsById, ...keyBy(droppedAssets, "_id") },
        isDirty: true,
      };
    });
  };

  const moveEntry = ({ fromIndex, toIndex }) => {
    setWorkingList((current) => {
      const isSamePosition = fromIndex === toIndex;
      const isOutOfRange =
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= current.entries.length ||
        toIndex >= current.entries.length;

      if (isSamePosition || isOutOfRange) return current;

      return {
        ...current,
        entries: arrayMove(current.entries, fromIndex, toIndex),
        isDirty: true,
      };
    });
  };

  const removeEntry = ({ entryKey }) => {
    setWorkingList((current) => ({
      ...current,
      entries: current.entries.filter((entry) => entry.entryKey !== entryKey),
      isDirty: true,
    }));
  };

  const openEntryPreview = ({ entryKey }) => setPreviewEntryKey(entryKey);

  const closeEntryPreview = () => setPreviewEntryKey(null);

  // Unlike an archive, a hard delete can never resolve again.
  const removeEntriesForAsset = ({ assetId }) => {
    setWorkingList((current) => ({
      ...current,
      entries: current.entries.filter((entry) => entry.asset_id !== assetId),
      isDirty: true,
    }));
  };

  // Caching a freshly fetched asset is not a user edit, so it must never set
  // isDirty — that would fire the unsaved-changes guard on a untouched list.
  const cacheAssets = ({ assets }) => {
    const cacheableAssets = (assets || []).filter(Boolean);
    if (isEmpty(cacheableAssets)) return;

    setWorkingList((current) => ({
      ...current,
      assetsById: { ...current.assetsById, ...keyBy(cacheableAssets, "_id") },
    }));
  };

  /**
   * @input {asset: {_id: 'a1', mediaType: 'video'}, inTime: 2, outTime: 5, sourceMedia: {frameRate: 24}}
   * @output true
   *
   * @input {asset: {_id: 'a2', mediaType: 'audio'}}
   * @output false
   */
  const addAssetToWorkingList = ({ asset, inTime, outTime, sourceMedia }) => {
    if (!isAssetsListMediaTypeAllowed(asset.mediaType)) {
      toast.error("Audio assets can't be added to a list");
      return false;
    }

    setWorkingList((current) => ({
      ...current,
      entries: [
        ...current.entries,
        createWorkingEntry({ asset, inTime, outTime, sourceMedia }),
      ],
      assetsById: { ...current.assetsById, [asset._id]: asset },
      isDirty: true,
    }));
    return true;
  };

  /**
   * Fills in what the player measured once an entry is opened. An entry
   * dragged straight into the strip has no player behind it and lands with
   * nothing measured, so the preview is the first chance to learn a clip's
   * frame rate and frame size — and the export needs both.
   *
   * Only a measurement that actually changed marks the list dirty; merely
   * opening a preview must not make an untouched list look edited.
   */
  const updateEntrySourceMedia = ({ entryKey, sourceMedia }) => {
    const measurements = toEntrySourceMedia(sourceMedia);

    setWorkingList((current) => {
      const targetEntry = current.entries.find(
        (entry) => entry.entryKey === entryKey,
      );
      if (!targetEntry) return current;

      // Never overwrite a real measurement with a null one: the player reports
      // duration and resolution on metadata and the frame rate only later, so
      // the first reading of a pair is always partial.
      const merged = omitBy(measurements, isNil);
      const hasNewMeasurement = some(
        merged,
        (value, field) => targetEntry[field] !== value,
      );
      if (!hasNewMeasurement) return current;

      return {
        ...current,
        entries: current.entries.map((entry) =>
          entry.entryKey === entryKey ? { ...entry, ...merged } : entry,
        ),
        isDirty: true,
      };
    });
  };

  // Targets the exact entry the preview was opened from — never every entry
  // for that asset — since the same asset may sit in the list more than once.
  const updateEntryTrim = ({ entryKey, inTime, outTime }) => {
    setWorkingList((current) => ({
      ...current,
      entries: current.entries.map((entry) =>
        entry.entryKey === entryKey
          ? { ...entry, in_time: inTime, out_time: outTime }
          : entry,
      ),
      isDirty: true,
    }));
  };

  const { downloadAssets, exporting: isDownloadingAssets } =
    useAssetsDownload(showId);

  const startNewList = () => {
    setPreviewEntryKey(null);
    setWorkingList((current) => ({
      ...createEmptyWorkingList(),
      assetsById: current.assetsById,
    }));
  };

  /**
   * Loading a different list retires whatever was on screen, but saving the
   * one already open must not — the panel closing under a Save reads as the
   * save having thrown something away.
   */
  const applySnapshot = ({ snapshot, preservePreviewedEntry = false }) => {
    setPreviewEntryKey(
      preservePreviewedEntry
        ? findPreservedPreviewEntryKey({
            previewEntryKey,
            previousEntries: workingList.entries,
            nextEntries: snapshot.entries,
          })
        : null,
    );
    setWorkingList((current) => ({
      assetsListId: snapshot.assetsListId,
      name: snapshot.name,
      visibility: snapshot.visibility,
      entries: snapshot.entries,
      assetsById: { ...current.assetsById, ...keyBy(snapshot.assets, "_id") },
      isDirty: false,
    }));
  };

  const loadList = async ({ assetsListId }) => {
    if (!showId) return;

    setIsLoading(true);
    try {
      /** @throws AssetsListApiError when the list is gone or not visible */
      const snapshot = await fetchAssetsList({ showId, assetsListId });
      applySnapshot({ snapshot });
    } catch (error) {
      console.error("Failed to load assets list", error);
      toast.error("Failed to load list");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * The snapshot rides along on a successful save so a caller chaining
   * straight into a download doesn't have to wait for this state update to
   * flush through React before it can read the id it just got back.
   *
   * @output {outcome: 'saved', snapshot} | {outcome: 'requiresSaveAs'}
   */
  const saveList = async () => {
    if (!showId) return { outcome: AssetsListSaveOutcome.RequiresSaveAs };
    // Never-saved lists have nothing to PUT. Whether the caller may write a
    // saved one is the api's call: its 404 falls through to Save As below.
    if (!workingList.assetsListId) {
      return { outcome: AssetsListSaveOutcome.RequiresSaveAs };
    }

    setIsSaving(true);
    try {
      /** @throws AssetsListApiError — 404 means the list is gone or not ours */
      const snapshot = await updateAssetsList({
        showId,
        assetsListId: workingList.assetsListId,
        name: workingList.name,
        visibility: workingList.visibility,
        entries: workingList.entries,
      });
      applySnapshot({ snapshot, preservePreviewedEntry: true });
      return { outcome: AssetsListSaveOutcome.Saved, snapshot };
    } catch (error) {
      if (error?.status === 404) {
        return { outcome: AssetsListSaveOutcome.RequiresSaveAs };
      }
      console.error("Failed to save assets list", error);
      toast.error(saveFailureMessage(error));
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // No client-side owner check: delete is owner-only on the api, which answers
  // 404 for anyone else — that response is what the user sees.
  /**
   * Deleting the list open in the strip leaves an empty new list behind —
   * otherwise the strip keeps pointing at an id that no longer exists. The
   * Load picker deletes through here too, for the same reason.
   *
   * @input {assetsListId: 'l1'}
   * @output undefined
   * @throws AssetsListApiError — 404 means the list is gone or not the caller's
   */
  const deleteList = async ({ assetsListId }) => {
    if (!showId) {
      throw new Error(
        `deleteList: no show is selected, so list ${assetsListId} can't be deleted`,
      );
    }
    if (!assetsListId) {
      throw new Error(`deleteList: no assetsListId given for show ${showId}`);
    }

    setIsSaving(true);
    try {
      await deleteAssetsList({ showId, assetsListId });
      if (workingList.assetsListId === assetsListId) {
        startNewList();
      }
    } catch (error) {
      console.error("Failed to delete assets list", error);
      if (error?.status === 404) {
        toast.error("Only the list's owner can delete it");
      } else {
        toast.error("Failed to delete list");
      }
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const saveListAs = async ({ name, visibility }) => {
    if (!showId) return { outcome: AssetsListSaveOutcome.RequiresSaveAs };

    setIsSaving(true);
    try {
      /** @throws AssetsListApiError on a rejected name / entries payload */
      const snapshot = await createAssetsList({
        showId,
        name,
        visibility,
        entries: workingList.entries,
      });
      applySnapshot({ snapshot, preservePreviewedEntry: true });
      return { outcome: AssetsListSaveOutcome.Saved, snapshot };
    } catch (error) {
      console.error("Failed to create assets list", error);
      toast.error(saveFailureMessage(error));
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // The edl route is keyed on an assetsListId and reads what the api stored, so
  // the download waits for a saved, unmodified list — a pending edit would ship
  // a sequence that disagrees with the assets packaged beside it.
  const canDownloadList = Boolean(
    showId &&
    workingList.assetsListId &&
    !workingList.isDirty &&
    !isEmpty(workingList.entries),
  );

  /**
   * @output {name: 'Opening.xml', text: '<?xml version="1.0"…'} | null
   */
  const fetchEditDecisionListFile = async ({ assetsListId, name }) => {
    try {
      const response = await apiFetch(
        `/api/shows/${showId}/assets-lists/${assetsListId}/download/edl`,
      );

      // 501 while generation is unbuilt, 404 for a list that is gone or not
      // visible — neither has a body worth putting in the archive.
      if (!response.ok) {
        toast.error(await readDownloadErrorMessage(response));
        return null;
      }

      const fallbackName = `${name || UNSAVED_ASSETS_LIST_LABEL}.xml`;
      const fileName =
        parseContentDispositionFilename(
          response.headers.get("Content-Disposition"),
        ) || fallbackName;

      return { name: fileName, text: await response.text() };
    } catch (error) {
      console.error("Failed to download edit decision list", error);
      toast.error("Failed to download EDL");
      return null;
    }
  };

  /**
   * One archive holding every asset in the list plus the edit decision list
   * beside them. These were two separate downloads, which left whoever opened
   * the edit to pair the two up by hand.
   *
   * One entry per drop, but the same asset can occupy several of them with
   * different trims — the zip only needs the underlying file once.
   *
   * Accepts an optional freshly-saved snapshot so a save-then-download flow
   * can use the id/name/entries it just got back instead of `workingList`,
   * which won't reflect the save until React flushes the state update.
   */
  const downloadList = async ({ snapshot } = {}) => {
    const assetsListId = snapshot?.assetsListId ?? workingList.assetsListId;
    const name = snapshot?.name ?? workingList.name;
    const entries = snapshot?.entries ?? workingList.entries;

    if (!showId || !assetsListId || isEmpty(entries)) return;

    setIsPreparingDownload(true);
    try {
      // Fetched before the zip starts: an edit decision list that cannot be
      // produced should abort the download, not yield a half-complete archive.
      const editDecisionListFile = await fetchEditDecisionListFile({
        assetsListId,
        name,
      });
      if (!editDecisionListFile) return;

      const assetIds = uniq(entries.map((entry) => entry.asset_id));
      downloadAssets(assetIds, name || UNSAVED_ASSETS_LIST_LABEL, [
        editDecisionListFile,
      ]);
    } finally {
      setIsPreparingDownload(false);
    }
  };

  const setExpanded = ({ expanded }) => setIsExpanded(expanded);

  // Like insertAssetsAt/moveEntry/removeEntry, this only edits the working
  // draft — the name is committed to the server by the existing Save button,
  // not on every keystroke.
  const renameWorkingList = ({ name }) => {
    setWorkingList((current) =>
      name === current.name ? current : { ...current, name, isDirty: true },
    );
  };

  return (
    <AssetsListContext.Provider
      value={{
        workingList,
        isExpanded,
        isSaving,
        isLoading,
        setExpanded,
        insertAssetsAt,
        updateEntrySourceMedia,
        moveEntry,
        removeEntry,
        removeEntriesForAsset,
        previewEntryKey,
        openEntryPreview,
        closeEntryPreview,
        cacheAssets,
        downloadList,
        canDownloadList,
        isDownloadingList: isPreparingDownload || isDownloadingAssets,
        addAssetToWorkingList,
        updateEntryTrim,
        startNewList,
        loadList,
        saveList,
        saveListAs,
        deleteList,
        renameWorkingList,
      }}>
      {children}
    </AssetsListContext.Provider>
  );
}

export function useAssetsList() {
  const context = useContext(AssetsListContext);
  if (!context) {
    throw new Error("useAssetsList must be used within an AssetsListProvider");
  }
  return context;
}
